// pelagos.js: the i-D FINEJEWELLERY behaviour layer on top of Prestige (spec §4.4, §6).
// Loaded as a module by layout/theme.liquid after Prestige's theme.js. It loads each Pelagos section's own module, and
// the libraries those need (GSAP, Embla), only on pages that use them (spec §7.2). Tested in tests/design/pelagos-js.test.mjs.

export const prefersReducedMotion = () => globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

// A section's focus handler acts only on keyboard focus, and not when the window gives focus back (section contract,
// "Focus handlers"). When a tab or window gets focus back, the browser fires focus and focusin again on the element
// that had it, which still matches :focus-visible if the keyboard focused it. This tells that refocus apart from a
// real move: on the window's blur it records the active element, and the next focusin on that same element is the
// refocus (once). Any other focusin clears the record. stop() removes the window listener. Given to every section
// module in the helpers below.
export function watchRefocus(win = globalThis.window, doc = globalThis.document) {
  let returning = null;
  const onBlur = () => {
    returning = doc?.activeElement ?? null;
  };
  win?.addEventListener?.('blur', onBlur);
  return {
    isRefocus(target) {
      const refocused = target != null && target === returning;
      returning = null;
      return refocused;
    },
    stop() {
      win?.removeEventListener?.('blur', onBlur);
      returning = null;
    },
  };
}

// Runs init(root) for the whole page now, and again for each section the theme editor re-renders.
export function onSectionLoad(init, doc = globalThis.document) {
  init(doc);
  doc.addEventListener('shopify:section:load', (event) => init(event.target));
}

/*
 * Click to open, hover to switch (spec §6, "Click-then-hover menus").
 * With the header's "Open menu on" set to Click, the first menu still needs a click. While any menu is open, every
 * menu in the primary navigation switches to Prestige's own hover mode, so pointing at another top-level item swaps
 * to its menu and moving away closes it after Prestige's grace delay. Once all menus are closed they go back to
 * click. Touch devices are unaffected: Prestige always uses click there.
 */
export const DISCLOSURES = ':is(mega-menu-disclosure, dropdown-menu-disclosure)';

export function syncMenuTriggers(nav) {
  const anyOpen = nav.querySelector('details[aria-expanded="true"]') !== null;
  nav.querySelectorAll(DISCLOSURES).forEach((disclosure) => disclosure.setAttribute('trigger', anyOpen ? 'hover' : 'click'));
}

export function initHoverSwitch(root) {
  const nav = root?.querySelector?.('.header__primary-nav');
  if (!nav || nav.hasAttribute('data-pelagos-hover-switch')) return false;
  const disclosures = [...nav.querySelectorAll(DISCLOSURES)];
  // Only when the header is set to open menus on click; with "hover" Prestige already does the rest.
  if (!disclosures.length || disclosures.some((disclosure) => disclosure.getAttribute('trigger') !== 'click')) return false;
  nav.setAttribute('data-pelagos-hover-switch', '');
  new globalThis.MutationObserver(() => syncMenuTriggers(nav))
    .observe(nav, { subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
  return true;
}

// Videos marked to autoplay (the Pelagos media block, and sections that reuse its class) start from here, muted, so a
// visitor who asks for reduced motion never sees them move: they get controls instead. A video the browser refuses to
// autoplay gets its controls too. Runs again whenever the visitor changes the setting. A video that also asks for its
// controls (class pelagos-media__video--controls: "Show controls" in the video with text section) keeps them while it
// plays. A video the visitor paused with its pause control (below) stays paused. The pause control follows each change.
// A video the browser does not draw (display: none: a phone video on a large screen, the large-screen video on a phone,
// a tile hidden on phones, a closed accordion or drawer) is not played, so it never downloads (with preload="none") and
// never runs unseen; it plays as soon as it is drawn, and stops when it no longer is (watchDrawing, below).
export const AUTOPLAY_VIDEOS = 'video.pelagos-media__video--autoplay';
const KEEPS_CONTROLS = 'pelagos-media__video--controls';
const PAUSED = 'data-pelagos-paused';

// Whether the browser draws the node: not display: none, nor inside such a box. Where checkVisibility() is missing
// (Safari before 17.4), a node with no client rects is not drawn; a node that offers neither counts as drawn.
export function isRendered(node) {
  if (typeof node?.checkVisibility === 'function') return node.checkVisibility();
  if (typeof node?.getClientRects === 'function') return node.getClientRects().length > 0;
  return true;
}

const autoplayVideos = (root) => (root?.querySelectorAll ? [...root.querySelectorAll(AUTOPLAY_VIDEOS)] : []);

// One video: stopped with its controls under reduced motion; stopped while the browser does not draw it; otherwise
// muted and playing, unless the visitor paused it with its pause control.
function applyMotionTo(video, reduce = prefersReducedMotion()) {
  if (reduce) {
    video.pause();
    showNativeControls(video);
    return;
  }
  const keepsControls = video.classList?.contains(KEEPS_CONTROLS) ?? false;
  if (!isRendered(video)) {
    video.pause();
    video.controls = keepsControls;
    refreshControl(video);
    return;
  }
  video.muted = true;
  video.controls = keepsControls;
  const chosePause = video.getAttribute?.(PAUSED) === 'true';
  if (chosePause) video.pause();
  // play() clears video.paused at once, so the button reads Pause; a refusal brings the native controls instead.
  const playing = chosePause ? null : Promise.resolve(video.play());
  refreshControl(video);
  playing?.catch(() => showNativeControls(video));
}

export function applyVideoMotion(root) {
  const videos = autoplayVideos(root);
  const reduce = prefersReducedMotion();
  for (const video of videos) applyMotionTo(video, reduce);
  return videos.length;
}

/*
 * Pause controls (WCAG 2.2.2): anything that moves by itself for more than 5 s needs a way to pause it. Every autoplay
 * video without its own controls, looping or not, and every animated image with a still source (a <picture> whose
 * <source data-pelagos-still media="…(prefers-reduced-motion: reduce)"> holds a single frame), gets a small labelled
 * button in a corner of its media (styled in assets/pelagos.css). It sits outside any link or button around the media,
 * so it is never nested in one, in the media's own box (its host), which pelagos.css positions unless the section
 * already does. Pausing a video pauses it; pausing an image switches it to its still. A video's button follows the
 * video's real state (its play, pause and ended events), and hides while the video shows its own controls (under
 * reduced motion, or when the browser refuses to play it, as in Low Power Mode). Under reduced motion every button
 * hides: the media is already still. The visitor's choice lasts for the page view. Runs from onSectionLoad, so
 * re-rendered sections get their buttons; a medium never gets a second one.
 * A <picture> can hold a phone image and a large-screen image, each animated or not. Each animated one has its own
 * still source, whose data-pelagos-still names the screens on which that image is the one shown (blank: every
 * screen). The button shows only on those screens and only for a video the browser draws; pausing switches every
 * still to its screens, so the image shown is the one that stops.
 * Labels: window.pelagosStrings (the theme's translations, set in snippets/pelagos-variables.liquid), else English.
 */
export const STILL_SOURCES = 'source[data-pelagos-still]';
const STILL_SCREENS = 'data-pelagos-still';
const STILL_MEDIA = 'data-pelagos-still-media'; // a still's own media, kept while the visitor pauses its image
export const MEDIA_STRINGS = {
  pauseVideo: 'Pause the video',
  playVideo: 'Play the video',
  pauseAnimation: 'Pause the animation',
  playAnimation: 'Play the animation',
};
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const HOST = 'data-pelagos-media-host';
const ICONS = {
  pause: '<svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" width="12" height="12"><path d="M3.5 1.5v9M8.5 1.5v9" stroke="currentColor" stroke-width="2" fill="none"/></svg>',
  play: '<svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" width="12" height="12"><path d="M3 1.5v9l7.5-4.5z" fill="currentColor"/></svg>',
};
const toggles = new WeakMap(); // medium → its pause button

const isVideo = (media) => media.tagName === 'VIDEO';
// A video's real state; an image's is the visitor's choice.
const isPaused = (media) => (isVideo(media) ? Boolean(media.paused) : media.getAttribute(PAUSED) === 'true');

export function pausableMedia(root) {
  const found = root?.querySelectorAll ? [...root.querySelectorAll(`${AUTOPLAY_VIDEOS}, ${STILL_SOURCES}`)] : [];
  const media = found
    .map((node) => (node.tagName === 'SOURCE' ? node.parentElement : node))
    .filter((medium) => medium && (!isVideo(medium) || !medium.classList.contains(KEEPS_CONTROLS)));
  return [...new Set(media)]; // a picture with a phone still and a large-screen still is one medium
}

const stillsOf = (picture) => [...(picture.querySelectorAll?.(STILL_SOURCES) ?? [])];
const screensOf = (still) => still.getAttribute(STILL_SCREENS) || 'all';
const onScreen = (screens) => screens === 'all' || (globalThis.matchMedia?.(screens).matches ?? true);

// Whether the medium moves where the visitor is: a video the browser draws, or a picture whose image shown on this
// screen is animated.
function movesHere(media) {
  return isVideo(media) ? isRendered(media) : stillsOf(media).some((still) => onScreen(screensOf(still)));
}

export function pauseLabel(media) {
  const text = { ...MEDIA_STRINGS, ...globalThis.pelagosStrings };
  if (isVideo(media)) return isPaused(media) ? text.playVideo : text.pauseVideo;
  return isPaused(media) ? text.playAnimation : text.pauseAnimation;
}

export function setMediaPaused(media, paused) {
  if (paused) media.setAttribute(PAUSED, 'true');
  else media.removeAttribute(PAUSED);
  if (isVideo(media)) {
    if (paused) media.pause();
    else if (!prefersReducedMotion()) Promise.resolve(media.play()).catch(() => showNativeControls(media));
    return;
  }
  for (const still of stillsOf(media)) {
    if (!still.hasAttribute(STILL_MEDIA)) still.setAttribute(STILL_MEDIA, still.media || REDUCED_MOTION);
    still.media = paused ? screensOf(still) : still.getAttribute(STILL_MEDIA);
  }
}

function renderControl(button, media) {
  button.hidden = prefersReducedMotion() || !movesHere(media) || (isVideo(media) && Boolean(media.controls));
  button.setAttribute('aria-label', pauseLabel(media));
  button.innerHTML = isPaused(media) ? ICONS.play : ICONS.pause;
}

function refreshControl(media) {
  const button = toggles.get(media);
  if (button) renderControl(button, media);
}

function showNativeControls(video) {
  video.controls = true;
  refreshControl(video);
}

// The outermost link or button around the media (a button placed inside one would be nested interactive content).
function outsideInteractive(media) {
  let outermost = media;
  for (let node = media.parentElement; node; node = node.parentElement) {
    if (node.tagName === 'A' || node.tagName === 'BUTTON') outermost = node;
  }
  return outermost;
}

export function addPauseControls(root, doc = globalThis.document) {
  let added = 0;
  for (const media of pausableMedia(root)) {
    const anchor = outsideInteractive(media);
    if (toggles.has(media) || !anchor.parentElement) continue;
    const button = doc.createElement('button');
    button.type = 'button';
    button.className = 'pelagos-media-toggle circle-button circle-button--sm';
    button.setAttribute('data-pelagos-media-toggle', '');
    button.addEventListener('click', () => {
      setMediaPaused(media, !isPaused(media));
      renderControl(button, media);
    });
    if (isVideo(media)) for (const type of ['play', 'pause', 'ended']) media.addEventListener(type, () => renderControl(button, media));
    anchor.parentElement.setAttribute(HOST, '');
    anchor.after(button);
    toggles.set(media, button);
    renderControl(button, media);
    added += 1;
  }
  return added;
}

export function syncPauseControls(root) {
  for (const media of pausableMedia(root)) refreshControl(media);
}

// Classic scripts, each loaded once per page (GSAP and ScrollTrigger ship as UMD files that set window globals).
const scripts = new Map();
export function loadScript(src, doc = globalThis.document) {
  if (!scripts.has(src)) {
    scripts.set(src, new Promise((resolve, reject) => {
      const script = doc.createElement('script');
      script.src = src;
      script.async = false;
      script.onload = () => resolve();
      script.onerror = () => {
        scripts.delete(src);
        reject(new Error(`pelagos: could not load ${src}`));
      };
      doc.head.append(script);
    }));
  }
  return scripts.get(src);
}

/*
 * Cart attributes set on the page (spec §6: the invoice fields and the gift message; Phase 5c, Task 5.1). One store per
 * page, given to their modules with the helpers below. Each value is a field of the cart form, so checking out sends
 * it, and is also saved at once through Shopify's /cart/update.js, as legacy did, so it is in the cart whatever way the
 * visitor leaves (the drawer's "View cart", an express checkout button). The store remembers the newest value set on
 * this page for each attribute: Prestige re-renders the cart drawer from the server on every cart change, and a render
 * made while a save was still on its way shows the older value, which the module then replaces (valueOf). Each save is
 * announced on the document, so another copy of the same field (the gift message is in the drawer and on the cart
 * page) follows. A failed save is reported in the console and changes nothing else: the form still sends the fields.
 * Saves go one at a time, each with only the attributes that changed: two requests on their way together can be
 * applied in either order, and an older one applied last overwrote newer values (seen on the dev theme). What is saved
 * meanwhile waits, merged, and goes when the request on its way is done. A failed value is tried once more at once,
 * unless a newer value of it has been sent or queued since (then it is dropped: retrying it would put the older value
 * back). What waits goes at once when the page is left (pagehide) and right before Prestige changes
 * the cart (cart:prepare-bundled-sections, line-item:will-change), sent with its cart/change.js: on the cart page a
 * quantity change reloads the page as soon as that request answers, and the reload can render before the save lands.
 * So the cart page's modules read the cart as Shopify holds it, once per page (serverCart), and fill what the page has
 * not set (has). Nothing is kept in the browser's storage: the server's cart is the one record across loads.
 */
export const CART_ATTRIBUTES_EVENT = 'pelagos:cart-attributes';
export function createCartAttributes({
  fetchImpl = (...args) => globalThis.fetch(...args),
  doc = globalThis.document,
  win = globalThis.window,
  root = () => globalThis.Shopify?.routes?.root ?? '/',
  log = globalThis.console,
} = {}) {
  const newest = new Map(); // attribute name → the newest value set on this page
  const sent = new Map(); // attribute name → the value last handed to Shopify (on its way, waiting, or saved)
  const generation = new Map(); // attribute name → a number raised by every new value handed to Shopify
  const retried = new Map(); // attribute name → the generation already tried again after a failure
  let waiting = null; // attributes to send once the request on its way is done
  let running = null; // the queue's drain while a request is on its way
  let serverCart = null; // the one read of the cart as Shopify holds it, made when first asked for
  const post = async (attributes) => {
    try {
      const response = await fetchImpl(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
        keepalive: true, // sent even when the page is left right after (the cart form submitted to checkout)
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      log?.error?.('pelagos: the cart attributes were not saved', error);
      return null;
    }
  };
  // The generation of each value of a batch, taken as it is sent.
  const generationsOf = (batch) => Object.fromEntries(Object.keys(batch).map((name) => [name, generation.get(name)]));
  // After a batch failed: a value that no newer one has replaced since (sent or queued, e.g. flushed while this one was
  // on its way) goes back in the queue once; one already retried is let go, so that saving it again sends it. A
  // replaced value is never sent again, whatever happened to the newer one. True when something was re-queued.
  const failed = (batch, at) => {
    let requeued = false;
    for (const [name, value] of Object.entries(batch)) {
      if (generation.get(name) !== at[name]) continue;
      if (retried.get(name) === at[name]) {
        sent.delete(name);
        continue;
      }
      retried.set(name, at[name]);
      waiting = { [name]: value, ...waiting };
      requeued = true;
    }
    return requeued;
  };
  const drain = async () => {
    let cart = null;
    while (waiting) {
      const batch = waiting;
      waiting = null;
      const at = generationsOf(batch);
      cart = await post(batch); // the first request starts at once, in the caller's turn
      if (cart === null) failed(batch, at);
    }
    running = null;
    return cart;
  };
  // What waits goes now, without waiting for the request on its way.
  const flush = () => {
    if (!waiting) return;
    const batch = waiting;
    waiting = null;
    const at = generationsOf(batch);
    post(batch).then((cart) => {
      if (cart === null && failed(batch, at)) running ??= drain();
    });
  };
  win?.addEventListener?.('pagehide', flush);
  for (const type of ['cart:prepare-bundled-sections', 'line-item:will-change']) doc?.addEventListener?.(type, flush);
  return {
    valueOf: (name, rendered) => (newest.has(name) ? newest.get(name) : rendered),
    // Whether the page has set this attribute (noted while typing, or saved), whatever the value.
    has: (name) => newest.has(name),
    note(name, value) {
      newest.set(name, value);
    },
    flush,
    // Resolves to the cart as Shopify holds it (/cart.js, past the browser's cache), read once per page however many
    // copies ask; null when it cannot be read, which is reported and never tried again.
    serverCart() {
      serverCart ??= (async () => {
        try {
          const response = await fetchImpl(`${root()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return await response.json();
        } catch (error) {
          log?.error?.('pelagos: the cart could not be read; the page keeps what it rendered', error);
          return null;
        }
      })();
      return serverCart;
    },
    // Resolves to the cart once these values are saved (null on failure or when nothing changed). source: the element
    // that saves, named in the announcement so that it can ignore its own.
    save(attributes, { source = null } = {}) {
      for (const [name, value] of Object.entries(attributes)) newest.set(name, value);
      doc?.dispatchEvent?.(new CustomEvent(CART_ATTRIBUTES_EVENT, { detail: { attributes: { ...attributes }, source } }));
      const changed = Object.entries(attributes).filter(([name, value]) => sent.get(name) !== value);
      if (!changed.length) return running ?? Promise.resolve(null);
      for (const [name, value] of changed) {
        sent.set(name, value);
        generation.set(name, (generation.get(name) ?? 0) + 1);
      }
      waiting = { ...waiting, ...Object.fromEntries(changed) };
      running ??= drain();
      return running;
    },
  };
}
export const cartAttributes = createCartAttributes();

/*
 * Section modules (spec §7.2: scripts load only on pages that use them).
 * A Pelagos section marks an element with data-pelagos-module="{{ 'pelagos-<name>.js' | asset_url }}". Each module is
 * imported once per page, and its default export is called once with the shared helpers. Modules define custom
 * elements, so sections that the theme editor adds or re-renders later upgrade by themselves. settleMotion finishes an
 * element's script animations at once (the dialogs' finisher below; the slideshow's slides under reduced motion).
 */
export const helpers = { prefersReducedMotion, loadScript, onSectionLoad, watchRefocus, cartAttributes, settleMotion: settleDialogMotion };
const modules = new Map();
export function loadSectionModules(root, importModule = (url) => import(url)) {
  const elements = root?.querySelectorAll ? [...root.querySelectorAll('[data-pelagos-module]')] : [];
  const urls = [...new Set(elements.map((element) => element.getAttribute('data-pelagos-module')).filter(Boolean))];
  return Promise.all(urls.map((url) => {
    if (!modules.has(url)) {
      modules.set(url, Promise.resolve(importModule(url))
        .then((module) => module.default?.(helpers))
        .catch((error) => {
          modules.delete(url);
          console.error(`pelagos: ${url} did not load`, error);
        }));
    }
    return modules.get(url);
  }));
}

/*
 * Drawn state. Whether the browser draws an autoplay video can change without the screen crossing a breakpoint: a
 * rule at 1000 px, an accordion or <details>, a drawer, a modal, a tab, a script toggling hidden. One ResizeObserver
 * per page watches every autoplay video. It reports a video whose box changes, including to and from 0×0 as the
 * browser stops or starts drawing it, and reports nothing while the page scrolls (an IntersectionObserver with a large
 * margin would report scrolling, and still need the margin to stand for "drawn"). Only a video whose drawn state
 * changed is acted on, through its own branch of the motion rules above: it plays, with its pause control, once drawn,
 * and stops once it is not. Any other report (a resize, a reflow) leaves it alone, so a video the visitor paused with
 * its own controls, or one that ended, is never restarted.
 * An animated image's pause control shows only on the screens its stills name (data-pelagos-still); each such media
 * query is listened to once per page, and the controls of the images that name it follow it.
 * A section the theme editor removes (shopify:section:unload) is let go; a video removed some other way is let go the
 * next time the browser reports it. Without ResizeObserver, a video keeps the state it had when its section loaded.
 */
export function watchDrawing({ Observer = globalThis.ResizeObserver, matchMedia = (query) => globalThis.matchMedia?.(query) } = {}) {
  const drawn = new WeakMap(); // video → whether the browser drew it when last seen
  const forgetVideo = (video) => {
    drawn.delete(video);
    observer?.unobserve(video);
  };
  const observer = typeof Observer === 'function' ? new Observer((entries) => {
    for (const { target: video } of entries) {
      if (video.isConnected === false) {
        forgetVideo(video);
        continue;
      }
      const now = isRendered(video);
      if (drawn.get(video) === now) continue;
      drawn.set(video, now);
      applyMotionTo(video);
    }
  }) : null;

  const screens = new Map(); // a still's screens query → the pictures whose pause control follows it
  const followScreens = (query) => {
    const pictures = new Set();
    screens.set(query, pictures);
    matchMedia(query)?.addEventListener?.('change', () => {
      for (const picture of pictures) {
        if (picture.isConnected === false) pictures.delete(picture);
        else refreshControl(picture);
      }
    });
    return pictures;
  };

  return {
    watch(root) {
      for (const video of observer ? autoplayVideos(root) : []) {
        if (drawn.has(video)) continue;
        drawn.set(video, isRendered(video));
        observer.observe(video);
      }
      for (const picture of pausableMedia(root).filter((media) => !isVideo(media))) {
        for (const query of stillsOf(picture).map(screensOf).filter((screens) => screens !== 'all')) {
          (screens.get(query) ?? followScreens(query)).add(picture);
        }
      }
    },
    forget(root) {
      for (const video of autoplayVideos(root)) forgetVideo(video);
      const media = pausableMedia(root);
      for (const pictures of screens.values()) for (const picture of media) pictures.delete(picture);
    },
  };
}

// The theme editor removes a section (before re-rendering it, or for good): its media are let go.
export function forgetOnUnload(watcher, doc = globalThis.document) {
  doc?.addEventListener?.('shopify:section:unload', (event) => watcher?.forget(event.target));
}

let pageWatcher = null; // the page's drawn-state watcher, set up once below

// Everything pelagos.js does for the page, and again for each section the theme editor re-renders.
export function initSection(root, watcher = pageWatcher) {
  initHoverSwitch(root);
  initGalleryZoom(root);
  applyVideoMotion(root);
  addPauseControls(root);
  watcher?.watch(root);
  loadSectionModules(root);
}

// When the visitor changes the motion setting, the videos and pause controls follow. Set up once per page. (A change
// of screen size needs nothing here: what it shows or hides, watchDrawing sees.)
export function watchMediaChanges(root = globalThis.document, matchMedia = (query) => globalThis.matchMedia?.(query)) {
  const refresh = () => {
    applyVideoMotion(root);
    syncPauseControls(root);
  };
  matchMedia(REDUCED_MOTION)?.addEventListener?.('change', refresh);
  return refresh;
}

// Prestige replaces the cart drawer's content with the server's render on every cart change, then fires
// cart-drawer:refreshed on it. The drawer of an empty cart holds no Pelagos element (the gift message is in its footer),
// so the first item added can bring a module the page has not loaded: load the drawer's (each is imported once).
export function watchCartDrawer(doc = globalThis.document, load = loadSectionModules) {
  doc?.addEventListener?.('cart-drawer:refreshed', (event) => load(event.target));
}

/*
 * Header colour per slide (spec §6 "Slideshow per-slide header colour", §7.1; Phase 5c, Task 6.2). The page's first
 * section under the transparent header (data-header-sync: the image carousel, assets/pelagos-image-carousel.js, and
 * Prestige's slideshow, assets/pelagos-slideshow.js) announces the colour its slide in view asks for: a bubbling
 * pelagos:header-color event, detail { color: 'light' | 'dark' | '' }, and a last '' to the document once taken down.
 * The header listens for that one signal here, on the document, from the start of the page (before any section module
 * loads, so the first colour is never missed): the newest colour goes on the page's root as data-pelagos-header-color,
 * which survives the theme editor re-rendering the header, and '' (no preference) takes it off. The first announcement
 * also marks the root (data-pelagos-header-synced): until then, the stylesheet shows the colour of the slide shown first,
 * so the first paint does not wait for the modules. The rules that draw the transparent header are in
 * snippets/pelagos-header-color.liquid, which Prestige's header renders. Returns the function that stops listening.
 */
export const HEADER_COLOR_EVENT = 'pelagos:header-color';
export const HEADER_COLOR = 'data-pelagos-header-color';
export const HEADER_SYNCED = 'data-pelagos-header-synced';
export function watchHeaderColor(doc = globalThis.document) {
  const onColor = (event) => {
    const color = event?.detail?.color;
    doc.documentElement?.setAttribute(HEADER_SYNCED, '');
    if (color === 'light' || color === 'dark') doc.documentElement?.setAttribute(HEADER_COLOR, color);
    else doc.documentElement?.removeAttribute(HEADER_COLOR);
  };
  doc?.addEventListener?.(HEADER_COLOR_EVENT, onColor);
  return () => doc?.removeEventListener?.(HEADER_COLOR_EVENT, onColor);
}

/*
 * Prestige's dialogs without their motion under reduced motion (spec §7.2; Phase 5c, Task 1's ruling for the filter
 * drawer, widened to every dialog in Task 6.2). Every Prestige dialog (its DialogElement: the cart drawer, the menu
 * drawer and its panels, quick buy, the filter drawer and its sort, the Request Price drawer and any other <x-drawer>
 * or <x-modal>, popovers, pop-ins, the search) fires dialog:before-show and dialog:before-hide, which do not bubble, so
 * they are listened to in the capture phase, right before it creates its opening or closing animations with Motion One,
 * which runs them through the Web Animations API: on the dialog itself (a modal on large screens, a pop-in, the menu's
 * panels), in its shadow root (a drawer's overlay and panel), and on its content (the menu's links, after
 * dialog:after-show). Under reduced motion they are finished at once: in a microtask, since they start in the same task
 * as the event, and again before the next frame, for those a handler starts once a promise settles (the cart drawer's
 * footer, after dialog:after-show). The open dialog around the one that fired is settled with it (the menu drawer
 * around its panels, whose links it reveals). Prestige's dialog then goes on as after any animation: it traps the focus,
 * and gives it back when it closes. Reduced motion is read at each event, so a change mid-visit needs no listener.
 */
export const DIALOG_EVENTS = ['dialog:before-show', 'dialog:after-show', 'dialog:before-hide'];

// A Prestige dialog that is open: an element with the open attribute, a shadow root and a hide() method (a <details
// open> has neither).
const isOpenDialog = (node) => Boolean(node?.hasAttribute?.('open') && node.shadowRoot && typeof node.hide === 'function');

// The outermost open dialog around `dialog` (itself when it is in none).
export function motionScope(dialog) {
  let scope = dialog;
  for (let node = dialog?.parentElement; node; node = node.parentElement) if (isOpenDialog(node)) scope = node;
  return scope;
}

// A script animation with an end: CSS transitions and animations (a hover, a spinner) are the page's own, and an
// endless animation cannot be finished.
const settles = (animation) => typeof animation.transitionProperty !== 'string' && typeof animation.animationName !== 'string'
  && animation.effect?.getComputedTiming?.().endTime !== Infinity;

// Finishes the dialog's running script animations at once: its own, its content's and its shadow root's.
export function settleDialogMotion(dialog) {
  const animations = [...(dialog?.getAnimations?.({ subtree: true }) ?? []), ...(dialog?.shadowRoot?.getAnimations?.() ?? [])];
  for (const animation of animations.filter(settles)) {
    try {
      animation.finish();
    } catch {
      // One that cannot finish (paused at a zero rate) goes on as Prestige made it; the others still finish.
    }
  }
}

export function watchDialogMotion(doc = globalThis.document, reduce = prefersReducedMotion, {
  queue = (task) => queueMicrotask(task),
  frame = (task) => globalThis.requestAnimationFrame?.(task),
} = {}) {
  const onEvent = (event) => {
    const dialog = event.target;
    if (!dialog || !reduce()) return;
    const settle = () => {
      const scope = motionScope(dialog);
      settleDialogMotion(dialog);
      if (scope !== dialog) settleDialogMotion(scope);
    };
    queue(settle);
    frame(settle);
  };
  for (const type of DIALOG_EVENTS) doc?.addEventListener?.(type, onEvent, true);
  return () => {
    for (const type of DIALOG_EVENTS) doc?.removeEventListener?.(type, onEvent, true);
  };
}

/**
 * Prestige's accordions and the header's menus without their motion under reduced motion (spec §7.2; Phase 5c's final
 * fix pass). Prestige's <accordion-disclosure> (the height and a 4px rise of its content) and the header's
 * <mega-menu-disclosure> and <dropdown-menu-disclosure> (a fade, the links staggered by 8px) animate their <details>
 * with Motion One when they open and close, and emit no event of their own. Opening, the <details> fires toggle (which
 * does not bubble, so it is listened to in the capture phase); opening and closing both set aria-expanded on it, in
 * the same task as the animations start (closing removes open only once they end), so aria-expanded is observed on the
 * whole page. Under reduced motion their animations are finished at once, as the dialogs' (above): in a microtask and
 * again before the next frame. Any other <details> is left alone. Reduced motion is read at each change.
 */
export const DISCLOSURE_HOSTS = ':is(accordion-disclosure, mega-menu-disclosure, dropdown-menu-disclosure)';
const isDisclosure = (node) => node?.tagName === 'DETAILS' && Boolean(node.parentElement?.matches?.(DISCLOSURE_HOSTS));

export function watchDisclosureMotion(doc = globalThis.document, reduce = prefersReducedMotion, {
  queue = (task) => queueMicrotask(task),
  frame = (task) => globalThis.requestAnimationFrame?.(task),
  Observer = globalThis.MutationObserver,
} = {}) {
  const settle = (details) => {
    if (!isDisclosure(details) || !reduce()) return;
    const finish = () => settleDialogMotion(details);
    queue(finish);
    frame(finish);
  };
  const onToggle = (event) => settle(event.target);
  doc?.addEventListener?.('toggle', onToggle, true);
  const observer = Observer && doc?.documentElement ? new Observer((records) => {
    for (const record of records) settle(record.target);
  }) : null;
  observer?.observe(doc.documentElement, { subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
  return () => {
    doc?.removeEventListener?.('toggle', onToggle, true);
    observer?.disconnect();
  };
}

/*
 * The keyboard and screen readers on Prestige's own controls (QA sweep 2, docs/rebuild/qa/sweep-2.md on branch
 * qa/sweep-2; QA fix batch D). Each one leaves Prestige's markup and look as they are.
 *
 * K3. From 1000 px Prestige opens the product gallery's lightbox only on a mouse click on one of its images, and hides
 * its zoom button there (md:hidden), so the keyboard cannot reach the zoom (WCAG 2.1.1). The zoom button
 * (<open-lightbox-button>: it opens the lightbox at the image shown, and the arrows go through the others) stays in the
 * tab order from 1000 px as well: visually hidden there (Prestige's .sr-only), and drawn in its corner while it has the
 * focus, so the gallery looks as Prestige made it to the mouse. Below 1000 px the button is Prestige's, as it was.
 * initGalleryZoom runs for each section, and watchGalleryZoom again when the screen crosses 1000 px.
 */
export const GALLERY_ZOOM = 'product-gallery[allow-zoom] .product-gallery__zoom-button';
export const LARGE_SCREEN = 'screen and (min-width: 1000px)';
const ZOOM = 'data-pelagos-zoom'; // 'large' or 'small': the screen the button was last set up for

export function initGalleryZoom(root, large = globalThis.matchMedia?.(LARGE_SCREEN).matches ?? false) {
  for (const button of root?.querySelectorAll?.(GALLERY_ZOOM) ?? []) {
    if (!button.hasAttribute(ZOOM)) {
      if (!button.classList.contains('md:hidden')) continue; // one Prestige already shows on large screens
      button.addEventListener('focus', () => {
        if (button.getAttribute(ZOOM) === 'large') button.classList.remove('sr-only');
      });
      button.addEventListener('blur', () => {
        if (button.getAttribute(ZOOM) === 'large') button.classList.add('sr-only');
      });
    }
    button.setAttribute(ZOOM, large ? 'large' : 'small');
    button.classList.toggle('md:hidden', !large);
    button.classList.toggle('sr-only', large && button.ownerDocument?.activeElement !== button);
  }
}

export function watchGalleryZoom(doc = globalThis.document, matchMedia = (query) => globalThis.matchMedia?.(query)) {
  const query = matchMedia(LARGE_SCREEN);
  const refresh = () => initGalleryZoom(doc, query?.matches ?? false);
  query?.addEventListener?.('change', refresh);
  return refresh;
}

/*
 * K4. PhotoSwipe, Prestige's lightbox, builds its dialog (.pswp, role="dialog") each time it opens, with no name. As
 * it is added to the page it takes the name of the gallery that opened it: the label Prestige gives the gallery's
 * carousel ("Product gallery", product.gallery.title).
 */
export const LIGHTBOX_OPEN = 'lightbox:open';
export function watchLightboxName(doc = globalThis.document, Observer = globalThis.MutationObserver) {
  let name = null;
  const onOpen = (event) => {
    name = event.target?.closest?.('product-gallery')?.querySelector('.product-gallery__carousel[aria-label]')?.getAttribute('aria-label') ?? null;
  };
  doc?.addEventListener?.(LIGHTBOX_OPEN, onOpen);
  const observer = Observer && doc?.body ? new Observer((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (name && node.classList?.contains('pswp') && !node.hasAttribute('aria-label')) node.setAttribute('aria-label', name);
      }
    }
  }) : null;
  observer?.observe(doc.body, { childList: true });
  return () => {
    doc?.removeEventListener?.(LIGHTBOX_OPEN, onOpen);
    observer?.disconnect();
  };
}

/*
 * K7. Below 1000 px a sub-menu of the menu drawer covers its main panel, which Prestige fades out, and Prestige leaves
 * the focus on the parent's button there, unseen until the next Tab. Once Prestige draws the sub-panel the button
 * opens (after its animation), the focus moves to the sub-panel's Back button, if it is still on the parent's button.
 * Back closes the sub-panel, and Prestige's focus trap gives the focus back to the parent's button.
 */
export const MENU_PARENT = 'header-sidebar .header-sidebar__main-panel .header-sidebar__linklist [aria-controls]';
export const SMALL_SCREEN = 'screen and (max-width: 999px)';
export function watchMenuPanels(doc = globalThis.document, {
  small = () => globalThis.matchMedia?.(SMALL_SCREEN).matches ?? false,
  frame = (task) => globalThis.requestAnimationFrame?.(task),
  now = () => globalThis.performance?.now?.() ?? Date.now(),
} = {}) {
  const onClick = (event) => {
    const button = event.target?.closest?.(MENU_PARENT);
    if (!button || !small()) return;
    const panel = doc.getElementById(button.getAttribute('aria-controls'));
    const until = now() + 2000;
    const moveFocus = () => {
      if (doc.activeElement !== button) return;
      const back = panel && !panel.hidden ? panel.querySelector('.header-sidebar__back-button') : null;
      if (back) back.focus();
      else if (now() < until) frame(moveFocus);
    };
    frame(moveFocus);
  };
  doc?.addEventListener?.('click', onClick);
  return () => doc?.removeEventListener?.('click', onClick);
}

if (typeof document !== 'undefined') {
  watchGalleryZoom(document);
  watchLightboxName(document);
  watchMenuPanels(document);
  watchHeaderColor(document);
  watchDialogMotion(document);
  watchDisclosureMotion(document);
  pageWatcher = watchDrawing();
  forgetOnUnload(pageWatcher, document);
  onSectionLoad(initSection);
  watchMediaChanges(document);
  watchCartDrawer(document);
}
